import type { FC } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useApolloClient, useMutation, useQuery } from "@apollo/client"

import styles from "./TaigaPage.module.scss"
import CreateSessionModal, { useIsMobile } from "./shared/CreateSessionModal"
import HandOverEmbedSessionModal from "./shared/HandOverEmbedSessionModal"
import MemoEditModal from "./shared/MemoEditModal"
import { appAlert, appConfirm } from "./shared/dialog"
import { type SessionStatusMap } from "./shared/sessionStatus"
import CreateMentionModal from "./slack_mentions/CreateMentionModal"
import { type MentionOverride } from "./slack_mentions/MentionRow"
import SetDueDateModal from "./slack_mentions/SetDueDateModal"
import SlackMentionsSection, {
	SlackMentionsSectionMentionFragment,
	SlackMentionsSectionSyncStateFragment,
} from "./slack_mentions/SlackMentionsSection"
import {
	type DueDateFilterValue,
	type DueDateSelectableValue,
	type PriorityFilterValue,
	type PrioritySelectableValue,
	type RcFilterValue,
	type SearchFilterValue,
	type SenderFilterValue,
	type SessionFilter,
	DEFAULT_SESSION_FILTER,
	DUE_DATE_FILTER_OPTIONS,
	PRIORITY_FILTER_OPTIONS,
	buildSlackFocusUrl,
	cx,
	getErrorMessage,
	isEditableElement,
	matchesDueDateFilter,
	matchesDueDateValue,
	matchesPriorityFilter,
	matchesPriorityValue,
	matchesSearchFilter,
	matchesSenderFilter,
	matchesSessionFilter,
	openSlackThread,
	loadSlackAppSetting,
	saveSlackAppSetting,
	type SlackAppSetting,
	copyTextToClipboard,
	loadTaigaFilterSetting,
	saveTaigaFilterSetting,
} from "./slack_mentions/utils"

import { useSalon } from "@/main/components/common_features/salon/useSalon"
import { getFragmentData } from "@/souhl_admin/gql/__generated__"
import { graphql } from "@/souhl_admin/gql/__generated__/gql"
import { formatJstDateUntilDayByHyphen } from "@/utils/date"

import type { SharedNode } from "./shared/NodeRow"
import type { NodeTreeFocusRequest } from "./slack_mentions/MentionNodeTree"

// メンバー追加時は go/modules/taiga/internal/domain/model/model.go の
// SlackUserToTaigaUserID と合わせて更新する。
// 「自分」判定は authUserId との比較で動的に行うため、この配列に isSelf は持たせない。
export const MENTION_USERS = [
	{
		taigaUserId: "72a1f5f4-bd86-4518-926b-c97f87731438",
		label: "河野",
	},
	{
		taigaUserId: "403b2794-9d52-4e58-abb2-41bb26f3c843",
		label: "小倉",
	},
	{
		taigaUserId: "018b49b3-f4a7-4fca-8294-48685e067a5c",
		label: "吉澤",
	},
	{
		taigaUserId: "565f294e-e5f6-46f6-8095-3b11b5c8956e",
		label: "横山",
	},
	{
		taigaUserId: "2744ca78-c001-70ef-9cd1-33a33957260a",
		label: "更田",
	},
] as const

export type MentionUser = (typeof MENTION_USERS)[number]

const TaigaSlackMentionMetaFragment = graphql(/* GraphQL */ `
	fragment TaigaSlackMentionMeta on TaigaSlackMention {
		id
		channelId
		threadTs
		messageTs
		permalink
		dueDate
		comment
		priority
		senderDisplayName
		embeddedNode {
			id
			isDone
			isArchived
			session {
				sessionStatus
				isRemoteControlEnabled
				memo
			}
		}
		childNodes {
			id
			isDone
			isArchived
			session {
				sessionStatus
				isRemoteControlEnabled
				memo
			}
		}
	}
`)

// mention が持つセッションノード(埋め込み session + session 付き child node)の
// status / remote control からフィルタ用のバケット有無を出す。
// セッションフィルタ(ビジー/アイドル/RC)の判定・件数集計で使う。
// status / isRemoteControlEnabled は polling 結果があればそれを優先する
// (画面に出ているバッジと同じ値でフィルタするため)。
// - hasBusy: BUSY 状態のセッションノードを1つ以上持つ
// - hasIdle: BUSY 以外 (IDLE / PENDING) のセッションノードを1つ以上持つ
// - hasRc: Remote Control 有効のセッションノードを1つ以上持つ。
//   done / archived になったノードの RC は「対応済み」なので数えない
//   (完了後も taiga_sessions.is_remote_control_enabled が true のまま残るため、
//    ノードの状態で生死を判定しないと RC フィルタに完了済み mention が混ざる)。
// hasBusy / hasIdle が両方 false ならセッションノードを持たないメンション。
type MentionSessionNode = {
	id: string
	isDone: boolean
	isArchived: boolean
	session?: {
		sessionStatus: string
		isRemoteControlEnabled: boolean
	} | null
}
const getMentionSessionBuckets = (
	meta: {
		embeddedNode: MentionSessionNode | null
		childNodes: readonly MentionSessionNode[]
	},
	polledSessionByNodeId: Record<
		string,
		{
			sessionStatus: string
			isRemoteControlEnabled: boolean
			memo: string | null
		}
	>
): { hasBusy: boolean; hasIdle: boolean; hasRc: boolean } => {
	const sessions: {
		sessionStatus: string
		isRemoteControlEnabled: boolean
		isNodeLive: boolean
	}[] = []
	const pushSession = (node: MentionSessionNode) => {
		if (!node.session) return
		const polled = polledSessionByNodeId[node.id]
		sessions.push({
			sessionStatus: polled?.sessionStatus ?? node.session.sessionStatus,
			isRemoteControlEnabled:
				polled?.isRemoteControlEnabled ?? node.session.isRemoteControlEnabled,
			isNodeLive: !node.isDone && !node.isArchived,
		})
	}
	if (meta.embeddedNode) pushSession(meta.embeddedNode)
	for (const node of meta.childNodes) pushSession(node)
	return {
		hasBusy: sessions.some((s) => s.sessionStatus === "BUSY"),
		hasIdle: sessions.some((s) => s.sessionStatus !== "BUSY"),
		hasRc: sessions.some((s) => s.isRemoteControlEnabled && s.isNodeLive),
	}
}

// メモ検索の対象テキストを集める。
// - mention のメモ (comment。編集中の optimistic override があればそちら)
// - mention 配下のノード (埋め込み session + child node) のセッションメモ
//   セッションメモは polling で更新されるため、polling 結果があればそれを優先する
//   (画面に出ている値と同じものを検索対象にする)。
const getMentionSearchTexts = (
	meta: {
		comment?: string | null
		embeddedNode: {
			id: string
			session?: { memo?: string | null } | null
		} | null
		childNodes: readonly {
			id: string
			session?: { memo?: string | null } | null
		}[]
	},
	comment: string | null | undefined,
	polledSessionByNodeId: Record<
		string,
		{
			sessionStatus: string
			isRemoteControlEnabled: boolean
			memo: string | null
		}
	>
): (string | null | undefined)[] => {
	const texts: (string | null | undefined)[] = [comment]
	const pushSessionMemo = (node: {
		id: string
		session?: { memo?: string | null } | null
	}) => {
		if (!node.session) return
		texts.push(polledSessionByNodeId[node.id]?.memo ?? node.session.memo)
	}
	if (meta.embeddedNode) pushSessionMemo(meta.embeddedNode)
	for (const node of meta.childNodes) pushSessionMemo(node)
	return texts
}

const GET_SLACK_MENTIONS = graphql(/* GraphQL */ `
	query GetSlackMentions($param: TaigaSlackMentionsParam!) {
		taigaSlackMentions(param: $param) {
			mentions {
				...TaigaSlackMentionMeta
				...SlackMentionsSectionMention
			}
		}
	}
`)

const GET_SLACK_MENTION_SYNC_STATE = graphql(/* GraphQL */ `
	query GetSlackMentionSyncState($param: TaigaSlackMentionSyncStateParam!) {
		taigaSlackMentionSyncState(param: $param) {
			syncState {
				...SlackMentionsSectionSyncState
			}
		}
	}
`)

const COMPLETE_SLACK_MENTION = graphql(/* GraphQL */ `
	mutation CompleteSlackMention($input: CompleteTaigaSlackMentionInput!) {
		completeTaigaSlackMention(input: $input) {
			mention {
				id
			}
		}
	}
`)

const SET_SLACK_MENTION_DUE_DATE = graphql(/* GraphQL */ `
	mutation SetSlackMentionDueDate($input: SetTaigaSlackMentionDueDateInput!) {
		setTaigaSlackMentionDueDate(input: $input) {
			mention {
				id
				dueDate
			}
		}
	}
`)

const PRIORITY_UP_SLACK_MENTION = graphql(/* GraphQL */ `
	mutation PriorityUpSlackMention($input: PriorityUpTaigaSlackMentionInput!) {
		priorityUpTaigaSlackMention(input: $input) {
			mention {
				id
				priority
			}
		}
	}
`)

const PRIORITY_DOWN_SLACK_MENTION = graphql(/* GraphQL */ `
	mutation PriorityDownSlackMention(
		$input: PriorityDownTaigaSlackMentionInput!
	) {
		priorityDownTaigaSlackMention(input: $input) {
			mention {
				id
				priority
			}
		}
	}
`)

const UPDATE_SLACK_MENTION_COMMENT = graphql(/* GraphQL */ `
	mutation UpdateSlackMentionComment(
		$input: UpdateTaigaSlackMentionCommentInput!
	) {
		updateTaigaSlackMentionComment(input: $input) {
			mention {
				id
				comment
			}
		}
	}
`)

const SYNC_SLACK_MENTIONS = graphql(/* GraphQL */ `
	mutation SyncTaigaSlackMentions($input: SyncTaigaSlackMentionsInput!) {
		syncTaigaSlackMentions(input: $input) {
			upsertedCount
		}
	}
`)

const CREATE_SLACK_MENTION = graphql(/* GraphQL */ `
	mutation CreateTaigaSlackMention($input: CreateTaigaSlackMentionInput!) {
		createTaigaSlackMention(input: $input) {
			mention {
				id
			}
		}
	}
`)

// Seq3: node ツリー関連 mutations
const CREATE_TAIGA_NODE_UNDER_SLACK_MENTION = graphql(/* GraphQL */ `
	mutation CreateTaigaNodeUnderSlackMention(
		$input: CreateTaigaNodeUnderSlackMentionInput!
	) {
		createTaigaNodeUnderSlackMention(input: $input) {
			node {
				id
				milestoneId
				parentNodeId
				name
				isArchived
				order
				isDone
				isCollapsed
			}
		}
	}
`)

const HAND_OVER_TAIGA_EMBED_SESSION = graphql(/* GraphQL */ `
	mutation HandOverTaigaEmbedSession($input: HandOverTaigaEmbedSessionInput!) {
		handOverTaigaEmbedSession(input: $input) {
			node {
				id
				milestoneId
				parentNodeId
				name
				isArchived
				order
				isDone
				isCollapsed
			}
		}
	}
`)

const TOGGLE_TAIGA_SLACK_MENTION_CHILD_NODES_COLLAPSED = graphql(/* GraphQL */ `
	mutation ToggleTaigaSlackMentionChildNodesCollapsed(
		$input: ToggleTaigaSlackMentionChildNodesCollapsedInput!
	) {
		toggleTaigaSlackMentionChildNodesCollapsed(input: $input) {
			mention {
				id
				isChildNodesCollapsed
			}
		}
	}
`)

const UPDATE_TAIGA_NODE = graphql(/* GraphQL */ `
	mutation UpdateTaigaNodeForMention($input: UpdateTaigaNodeInput!) {
		updateTaigaNode(input: $input) {
			node {
				id
				name
				isCollapsed
				isDone
			}
		}
	}
`)

const ARCHIVE_TAIGA_NODE = graphql(/* GraphQL */ `
	mutation ArchiveTaigaNodeForMention($input: ArchiveTaigaNodeInput!) {
		archiveTaigaNode(input: $input) {
			node {
				id
				isArchived
			}
		}
	}
`)

const CREATE_TAIGA_NODE = graphql(/* GraphQL */ `
	mutation CreateTaigaNodeForMention($input: CreateTaigaNodeInput!) {
		createTaigaNode(input: $input) {
			node {
				id
				milestoneId
				parentNodeId
				name
				isArchived
				order
				isDone
				isCollapsed
			}
		}
	}
`)

// Seq3: node ツリー操作用 mutations (TaigaSheetDetailPage と同じ操作を実現)
const PRIORITY_UP_TAIGA_NODE = graphql(/* GraphQL */ `
	mutation PriorityUpTaigaNodeForMention($input: PriorityUpTaigaNodeInput!) {
		priorityUpTaigaNode(input: $input) {
			updatedNodes {
				id
				parallelPriority
			}
		}
	}
`)

const PRIORITY_DOWN_TAIGA_NODE = graphql(/* GraphQL */ `
	mutation PriorityDownTaigaNodeForMention(
		$input: PriorityDownTaigaNodeInput!
	) {
		priorityDownTaigaNode(input: $input) {
			updatedNodes {
				id
				parallelPriority
			}
		}
	}
`)

const CREATE_TAIGA_SESSION = graphql(/* GraphQL */ `
	mutation CreateTaigaSessionForMention($input: CreateTaigaSessionInput!) {
		createTaigaSession(input: $input) {
			node {
				id
				isDone
				session {
					threadTs
					channelId
					sessionStatus
					memo
					ccSessionId
					claudeRemoteControlSessionId
				}
			}
		}
	}
`)

const COMPLETE_TAIGA_SESSION = graphql(/* GraphQL */ `
	mutation CompleteTaigaSessionForMention($input: CompleteTaigaSessionInput!) {
		completeTaigaSession(input: $input) {
			node {
				id
				isDone
				session {
					threadTs
					channelId
					sessionStatus
					memo
					ccSessionId
					claudeRemoteControlSessionId
				}
			}
		}
	}
`)

const FORK_TAIGA_SESSION = graphql(/* GraphQL */ `
	mutation ForkTaigaSessionForMention($input: ForkTaigaSessionInput!) {
		forkTaigaSession(input: $input) {
			node {
				id
				isDone
				session {
					threadTs
					channelId
					sessionStatus
					memo
					ccSessionId
					claudeRemoteControlSessionId
				}
			}
		}
	}
`)

const UPDATE_TAIGA_SESSION_STATUS = graphql(/* GraphQL */ `
	mutation UpdateTaigaSessionStatusForMention(
		$input: UpdateTaigaSessionStatusInput!
	) {
		updateTaigaSessionStatus(input: $input) {
			node {
				id
				isDone
				session {
					threadTs
					channelId
					sessionStatus
					memo
					ccSessionId
					claudeRemoteControlSessionId
				}
			}
		}
	}
`)

const UPDATE_TAIGA_SESSION_MEMO = graphql(/* GraphQL */ `
	mutation UpdateTaigaSessionMemoForMention(
		$input: UpdateTaigaSessionMemoInput!
	) {
		updateTaigaSessionMemo(input: $input) {
			node {
				id
				isDone
				session {
					threadTs
					channelId
					sessionStatus
					memo
					ccSessionId
					claudeRemoteControlSessionId
				}
			}
		}
	}
`)

const MOVE_TAIGA_NODE = graphql(/* GraphQL */ `
	mutation MoveTaigaNodeForMention($input: MoveTaigaNodeInput!) {
		moveTaigaNode(input: $input) {
			node {
				id
				milestoneId
				parentNodeId
				order
			}
		}
	}
`)

const REORDER_TAIGA_NODES = graphql(/* GraphQL */ `
	mutation ReorderTaigaNodesForMention($input: ReorderTaigaNodesInput!) {
		reorderTaigaNodes(input: $input) {
			nodes {
				id
				order
			}
		}
	}
`)

const ATTACH_SLACK_THREAD_FOR_MENTION = graphql(/* GraphQL */ `
	mutation AttachTaigaSlackThreadForMention(
		$input: AttachTaigaSlackThreadInput!
	) {
		attachTaigaSlackThread(input: $input) {
			node {
				id
				isDone
				session {
					threadTs
					channelId
					sessionStatus
					memo
					ccSessionId
					claudeRemoteControlSessionId
				}
			}
		}
	}
`)

const GET_LATEST_THREAD_TS_FOR_MENTION = graphql(/* GraphQL */ `
	query GetLatestThreadTsForMention($param: TaigaLatestThreadTsParam!) {
		taigaLatestThreadTs(param: $param) {
			ts
		}
	}
`)

const GET_SLACK_LATEST_REPLY_TS = graphql(/* GraphQL */ `
	query GetSlackLatestReplyTs($param: SlackLatestReplyTsParam!) {
		slackLatestReplyTs(param: $param) {
			ts
		}
	}
`)

const GET_SESSION_BRANCH_NAME_FOR_MENTION = graphql(/* GraphQL */ `
	query GetSessionBranchNameForMention($param: TaigaSessionBranchNameParam!) {
		taigaSessionBranchName(param: $param) {
			branchName
		}
	}
`)

type PriorityFocusInfo = {
	id: string
	index: number
	fallbackId: string | null
}

// 一覧から消えた mention に代わってフォーカスする mention を選ぶ。
// 消える直前の並び (prevIds) を基準に「1 個下」を優先し、無ければ「1 個上」へ
// 順に辿って、現在も表示中 (currentIds) の最初の mention を返す。
const getAdjacentVisibleMentionId = (
	prevIds: string[],
	removedId: string,
	currentIds: string[]
): string | null => {
	const prevIndex = prevIds.indexOf(removedId)
	if (prevIndex === -1) return null
	for (let i = prevIndex + 1; i < prevIds.length; i++) {
		if (currentIds.includes(prevIds[i])) return prevIds[i]
	}
	for (let i = prevIndex - 1; i >= 0; i--) {
		if (currentIds.includes(prevIds[i])) return prevIds[i]
	}
	return null
}

type TaigaMentionsSectionProps = {
	isExpanded: boolean
	onToggleExpanded: () => void
	activeUserId: string | null
	onActiveUserIdChange: (userId: string) => void
	// session status polling (親 TaigaPage で集約) の受け渡し用。
	sessionStatusMap: SessionStatusMap
	onEmbeddedNodeIdsChange: (nodeIds: string[]) => void
}

const TaigaMentionsSection: FC<TaigaMentionsSectionProps> = ({
	isExpanded,
	onToggleExpanded,
	activeUserId,
	onActiveUserIdChange,
	sessionStatusMap,
	onEmbeddedNodeIdsChange,
}) => {
	const isKnownMentionUser = (id: string | null): id is string =>
		id !== null && MENTION_USERS.some((u) => u.taigaUserId === id)

	const effectiveActiveUserId = isKnownMentionUser(activeUserId)
		? activeUserId
		: MENTION_USERS[0].taigaUserId

	// embed 引き継ぎモーダルの対象 mention（null = 非表示）
	const [handOverMentionId, setHandOverMentionId] = useState<string | null>(
		null
	)
	const [focusedMentionId, setFocusedMentionId] = useState<string | null>(null)
	const [editingMentionId, setEditingMentionId] = useState<string | null>(null)
	const [editingComment, setEditingComment] = useState("")
	const [hiddenMentionIds, setHiddenMentionIds] = useState<Set<string>>(
		() => new Set()
	)
	const [mentionOverrides, setMentionOverrides] = useState<
		Record<string, MentionOverride>
	>({})
	// 優先度・期限フィルタは複数選択 (OR)。空配列 = 全件表示。
	// 初期値は localStorage から復元し、次に開いたときも前回の filter を引き継ぐ。
	const [priorityFilter, setPriorityFilter] = useState<
		PrioritySelectableValue[]
	>(() => loadTaigaFilterSetting().priority)
	const [dueDateFilter, setDueDateFilter] = useState<DueDateSelectableValue[]>(
		() => loadTaigaFilterSetting().dueDate
	)
	const [senderFilter, setSenderFilter] = useState<SenderFilterValue>(
		() => loadTaigaFilterSetting().sender
	)
	// セッション(作業状態)フィルタ。ビジー/アイドルの独立トグル。両方 true = 全件。
	const [sessionFilter, setSessionFilter] = useState<SessionFilter>(
		() => loadTaigaFilterSetting().session
	)
	// メモ検索キーワード。空文字 = 検索なし。
	const [searchKeyword, setSearchKeyword] = useState<SearchFilterValue>(
		() => loadTaigaFilterSetting().search
	)
	// filter 変更のたびに localStorage へ保存する（フィールド単位で書き戻す）。
	// storage イベントは購読しないので、別 window で filter を変えても
	// この window の表示が勝手に変わることはない（次に開いた window が最新を読む）。
	useEffect(() => {
		saveTaigaFilterSetting({ priority: priorityFilter })
	}, [priorityFilter])
	useEffect(() => {
		saveTaigaFilterSetting({ dueDate: dueDateFilter })
	}, [dueDateFilter])
	useEffect(() => {
		saveTaigaFilterSetting({ sender: senderFilter })
	}, [senderFilter])
	useEffect(() => {
		saveTaigaFilterSetting({ session: sessionFilter })
	}, [sessionFilter])
	useEffect(() => {
		saveTaigaFilterSetting({ search: searchKeyword })
	}, [searchKeyword])
	const [slackAppSetting, setSlackAppSetting] =
		useState<SlackAppSetting>(loadSlackAppSetting)
	const isMobile = useIsMobile()
	const handleChangeSlackAppEnabled = useCallback((enabled: boolean) => {
		const next = { enabled }
		saveSlackAppSetting(next)
		setSlackAppSetting(next)
	}, [])

	// 選択肢をトグルする。null (全部/全て) は選択解除 = 空配列にする。
	const handleTogglePriorityFilter = useCallback(
		(value: PriorityFilterValue) => {
			if (value === null) {
				setPriorityFilter([])
				return
			}
			setPriorityFilter((current) =>
				current.includes(value)
					? current.filter((v) => v !== value)
					: [...current, value]
			)
		},
		[]
	)
	const handleToggleDueDateFilter = useCallback((value: DueDateFilterValue) => {
		if (value === null) {
			setDueDateFilter([])
			return
		}
		setDueDateFilter((current) =>
			current.includes(value)
				? current.filter((v) => v !== value)
				: [...current, value]
		)
	}, [])
	// ビジー/アイドルのトグルを切り替える。
	const handleToggleSessionFilter = useCallback((kind: "busy" | "idle") => {
		setSessionFilter((current) => ({ ...current, [kind]: !current[kind] }))
	}, [])
	// RC フィルタ (すべて / RC / RC外) を切り替える。排他選択。
	const handleChangeRcFilter = useCallback((value: RcFilterValue) => {
		setSessionFilter((current) => ({ ...current, rc: value }))
	}, [])
	const pendingPriorityFocusRef = useRef<PriorityFocusInfo | null>(null)
	// 直前の表示中 mention の並び (id 配列)。フォーカス中の mention が一覧から
	// 消えたときに「1 個下 → 無ければ 1 個上」を辿る基準として使う。
	const previousVisibleIdsRef = useRef<string[]>([])

	// 認証ユーザー自身の taiga user_id (Cognito id_token の sub = idp users.id)。
	// SalonProvider が fetchAuthSession から取り出してくれるので、それを使う。
	const { currentUserID: authUserId } = useSalon()
	// active user_id が認証ユーザーでない場合に read-only。
	const isReadOnlyMentionTab = effectiveActiveUserId !== authUserId

	// 自分のアクティブタブのみ mentions を定期 polling する (他人/非アクティブタブは 0 = 停止)。
	// 手動作成 (maki 宛メンション) や Slack 側での作成では embed session が「非同期で後から」
	// mention に紐づくため、作成直後の 1 回 refetch では session が付いていない。polling で
	// mention を引き直すことで、リロードせずに embed session の表示と busy/idle 判定に反映する。
	const getMentionsPollInterval = (userId: string) =>
		effectiveActiveUserId === userId && authUserId === userId ? 5000 : 0

	// 各ユーザー (河野・小倉・吉澤・横山・更田) のメンションを並列で取得する。
	// hooks rule のためループにできないので手動で展開する。
	// メンバー追加時は MENTION_USERS と合わせてここも増やす。
	const konoMentionsQuery = useQuery(GET_SLACK_MENTIONS, {
		variables: { param: { userId: MENTION_USERS[0].taigaUserId } },
		fetchPolicy: "cache-and-network",
		pollInterval: getMentionsPollInterval(MENTION_USERS[0].taigaUserId),
	})
	const oguraMentionsQuery = useQuery(GET_SLACK_MENTIONS, {
		variables: { param: { userId: MENTION_USERS[1].taigaUserId } },
		fetchPolicy: "cache-and-network",
		pollInterval: getMentionsPollInterval(MENTION_USERS[1].taigaUserId),
	})
	const yoshizawaMentionsQuery = useQuery(GET_SLACK_MENTIONS, {
		variables: { param: { userId: MENTION_USERS[2].taigaUserId } },
		fetchPolicy: "cache-and-network",
		pollInterval: getMentionsPollInterval(MENTION_USERS[2].taigaUserId),
	})
	const yokoyamaMentionsQuery = useQuery(GET_SLACK_MENTIONS, {
		variables: { param: { userId: MENTION_USERS[3].taigaUserId } },
		fetchPolicy: "cache-and-network",
		pollInterval: getMentionsPollInterval(MENTION_USERS[3].taigaUserId),
	})
	const sarataMentionsQuery = useQuery(GET_SLACK_MENTIONS, {
		variables: { param: { userId: MENTION_USERS[4].taigaUserId } },
		fetchPolicy: "cache-and-network",
		pollInterval: getMentionsPollInterval(MENTION_USERS[4].taigaUserId),
	})
	// 自分 (auth user) のアクティブ mention タブのみ 1 分ごとに sync_state を polling する。
	// 他人タブでは polling しない (read-only タブで無駄な通信を避ける)。
	const getSyncStatePollInterval = (userId: string) =>
		effectiveActiveUserId === userId && authUserId === userId ? 60000 : 0
	const konoSyncStateQuery = useQuery(GET_SLACK_MENTION_SYNC_STATE, {
		variables: { param: { userId: MENTION_USERS[0].taigaUserId } },
		fetchPolicy: "cache-and-network",
		pollInterval: getSyncStatePollInterval(MENTION_USERS[0].taigaUserId),
	})
	const oguraSyncStateQuery = useQuery(GET_SLACK_MENTION_SYNC_STATE, {
		variables: { param: { userId: MENTION_USERS[1].taigaUserId } },
		fetchPolicy: "cache-and-network",
		pollInterval: getSyncStatePollInterval(MENTION_USERS[1].taigaUserId),
	})
	const yoshizawaSyncStateQuery = useQuery(GET_SLACK_MENTION_SYNC_STATE, {
		variables: { param: { userId: MENTION_USERS[2].taigaUserId } },
		fetchPolicy: "cache-and-network",
		pollInterval: getSyncStatePollInterval(MENTION_USERS[2].taigaUserId),
	})
	const yokoyamaSyncStateQuery = useQuery(GET_SLACK_MENTION_SYNC_STATE, {
		variables: { param: { userId: MENTION_USERS[3].taigaUserId } },
		fetchPolicy: "cache-and-network",
		pollInterval: getSyncStatePollInterval(MENTION_USERS[3].taigaUserId),
	})
	const sarataSyncStateQuery = useQuery(GET_SLACK_MENTION_SYNC_STATE, {
		variables: { param: { userId: MENTION_USERS[4].taigaUserId } },
		fetchPolicy: "cache-and-network",
		pollInterval: getSyncStatePollInterval(MENTION_USERS[4].taigaUserId),
	})

	const mentionsQueriesByUserId = useMemo(
		() => ({
			[MENTION_USERS[0].taigaUserId]: konoMentionsQuery,
			[MENTION_USERS[1].taigaUserId]: oguraMentionsQuery,
			[MENTION_USERS[2].taigaUserId]: yoshizawaMentionsQuery,
			[MENTION_USERS[3].taigaUserId]: yokoyamaMentionsQuery,
			[MENTION_USERS[4].taigaUserId]: sarataMentionsQuery,
		}),
		[
			konoMentionsQuery,
			oguraMentionsQuery,
			yoshizawaMentionsQuery,
			yokoyamaMentionsQuery,
			sarataMentionsQuery,
		]
	)
	const syncStateQueriesByUserId = useMemo(
		() => ({
			[MENTION_USERS[0].taigaUserId]: konoSyncStateQuery,
			[MENTION_USERS[1].taigaUserId]: oguraSyncStateQuery,
			[MENTION_USERS[2].taigaUserId]: yoshizawaSyncStateQuery,
			[MENTION_USERS[3].taigaUserId]: yokoyamaSyncStateQuery,
			[MENTION_USERS[4].taigaUserId]: sarataSyncStateQuery,
		}),
		[
			konoSyncStateQuery,
			oguraSyncStateQuery,
			yoshizawaSyncStateQuery,
			yokoyamaSyncStateQuery,
			sarataSyncStateQuery,
		]
	)

	const activeMentionsQuery = mentionsQueriesByUserId[effectiveActiveUserId]
	const activeSyncStateQuery = syncStateQueriesByUserId[effectiveActiveUserId]

	// 自分タブ表示中だけ、polling で取得した sync_state の lastSyncTime が変わったら
	// 自分の mentions を引き直す (バックエンドの sync job が新規メンションを保存した時など)。
	const isActiveOwnTab = !!(authUserId && effectiveActiveUserId === authUserId)
	const ownMentionsQuery = authUserId
		? mentionsQueriesByUserId[authUserId]
		: undefined
	const ownSyncStateQuery = authUserId
		? syncStateQueriesByUserId[authUserId]
		: undefined
	const ownSyncStateUnmasked = getFragmentData(
		SlackMentionsSectionSyncStateFragment,
		ownSyncStateQuery?.data?.taigaSlackMentionSyncState.syncState
	)
	const ownLastSyncTime = isActiveOwnTab
		? (ownSyncStateUnmasked?.lastSyncTime ?? null)
		: null
	const refetchOwnMentions = ownMentionsQuery?.refetch
	const previousOwnLastSyncTimeRef = useRef<string | null>(null)
	useEffect(() => {
		if (!isActiveOwnTab) {
			previousOwnLastSyncTimeRef.current = null
			return
		}
		if (!ownLastSyncTime) return
		if (previousOwnLastSyncTimeRef.current === null) {
			previousOwnLastSyncTimeRef.current = ownLastSyncTime
			return
		}
		if (previousOwnLastSyncTimeRef.current !== ownLastSyncTime) {
			previousOwnLastSyncTimeRef.current = ownLastSyncTime
			void refetchOwnMentions?.()
		}
	}, [isActiveOwnTab, ownLastSyncTime, refetchOwnMentions])

	const slackMentionsData = activeMentionsQuery?.data
	const slackMentionsLoading = activeMentionsQuery?.loading ?? false
	const slackMentionsError = activeMentionsQuery?.error
	const refetchSlackMentions = activeMentionsQuery?.refetch
	const syncStateData = activeSyncStateQuery?.data
	const syncStateLoading = activeSyncStateQuery?.loading ?? false
	const syncStateError = activeSyncStateQuery?.error

	const [completeSlackMention] = useMutation(COMPLETE_SLACK_MENTION)
	const [setSlackMentionDueDate] = useMutation(SET_SLACK_MENTION_DUE_DATE)
	const [priorityUpSlackMention] = useMutation(PRIORITY_UP_SLACK_MENTION)
	const [priorityDownSlackMention] = useMutation(PRIORITY_DOWN_SLACK_MENTION)
	const [updateSlackMentionComment] = useMutation(UPDATE_SLACK_MENTION_COMMENT)
	const [syncSlackMentions, { loading: isSyncing }] = useMutation(
		SYNC_SLACK_MENTIONS,
		{
			refetchQueries: [GET_SLACK_MENTIONS, GET_SLACK_MENTION_SYNC_STATE],
			awaitRefetchQueries: true,
		}
	)
	const [createSlackMention, { loading: isCreating }] =
		useMutation(CREATE_SLACK_MENTION)
	// Seq3: node ツリー関連 mutations
	const [createNodeUnderSlackMention, { loading: createRootNodeLoading }] =
		useMutation(CREATE_TAIGA_NODE_UNDER_SLACK_MENTION, {
			refetchQueries: [GET_SLACK_MENTIONS],
			awaitRefetchQueries: true,
		})
	const [handOverEmbedSession, { loading: handOverLoading }] = useMutation(
		HAND_OVER_TAIGA_EMBED_SESSION,
		{
			refetchQueries: [GET_SLACK_MENTIONS],
			awaitRefetchQueries: true,
		}
	)
	const [toggleChildNodesCollapsed] = useMutation(
		TOGGLE_TAIGA_SLACK_MENTION_CHILD_NODES_COLLAPSED
	)
	const [updateTaigaNode] = useMutation(UPDATE_TAIGA_NODE)
	const [archiveTaigaNode] = useMutation(ARCHIVE_TAIGA_NODE, {
		refetchQueries: [GET_SLACK_MENTIONS],
		awaitRefetchQueries: true,
	})
	const [createTaigaNode, { loading: createChildNodeLoading }] = useMutation(
		CREATE_TAIGA_NODE,
		{
			refetchQueries: [GET_SLACK_MENTIONS],
			awaitRefetchQueries: true,
		}
	)
	// Seq3: node ツリー操作用 mutations (TaigaSheetDetailPage と同じ操作を実現)
	const apolloClient = useApolloClient()
	const [priorityUpTaigaNode] = useMutation(PRIORITY_UP_TAIGA_NODE, {
		refetchQueries: [GET_SLACK_MENTIONS],
		awaitRefetchQueries: true,
	})
	const [priorityDownTaigaNode] = useMutation(PRIORITY_DOWN_TAIGA_NODE, {
		refetchQueries: [GET_SLACK_MENTIONS],
		awaitRefetchQueries: true,
	})
	const [createTaigaSession, { loading: createSessionLoading }] = useMutation(
		CREATE_TAIGA_SESSION,
		{
			refetchQueries: [GET_SLACK_MENTIONS],
			awaitRefetchQueries: true,
		}
	)
	const [forkTaigaSession] = useMutation(FORK_TAIGA_SESSION, {
		refetchQueries: [GET_SLACK_MENTIONS],
		awaitRefetchQueries: true,
	})
	const [completeTaigaSession] = useMutation(COMPLETE_TAIGA_SESSION, {
		refetchQueries: [GET_SLACK_MENTIONS],
		awaitRefetchQueries: true,
	})
	const [updateTaigaSessionStatus] = useMutation(UPDATE_TAIGA_SESSION_STATUS, {
		refetchQueries: [GET_SLACK_MENTIONS],
		awaitRefetchQueries: true,
	})
	const [updateTaigaSessionMemo, { loading: updateMemoLoading }] = useMutation(
		UPDATE_TAIGA_SESSION_MEMO,
		{
			refetchQueries: [GET_SLACK_MENTIONS],
			awaitRefetchQueries: true,
		}
	)
	const [moveTaigaNode] = useMutation(MOVE_TAIGA_NODE, {
		refetchQueries: [GET_SLACK_MENTIONS],
		awaitRefetchQueries: true,
	})
	const [reorderTaigaNodes] = useMutation(REORDER_TAIGA_NODES, {
		refetchQueries: [GET_SLACK_MENTIONS],
		awaitRefetchQueries: true,
	})
	// Session/Memo modal state for mention node tree (using shared components)
	// existingNode: 既存ノードへの session 作成 / newRootNode・newChildNode: モーダルから
	// prompt を入力してノードを新規作成する (Create Session で node + session を一度に作成)。
	const [nodeSessionModal, setNodeSessionModal] = useState<
		| { kind: "existingNode"; nodeId: string; nodeName: string }
		| { kind: "newRootNode"; mentionId: string }
		| {
				kind: "newChildNode"
				milestoneId: string | null
				parentNodeId: string
		  }
		| null
	>(null)
	const [nodeMemoModal, setNodeMemoModal] = useState<SharedNode | null>(null)
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
	// 期限設定モーダルの対象 mention id (null = 閉じている)
	const [dueDateModalMentionId, setDueDateModalMentionId] = useState<
		string | null
	>(null)

	// Attach Slack thread mutation
	const [attachSlackThread, { loading: attachLoading }] = useMutation(
		ATTACH_SLACK_THREAD_FOR_MENTION,
		{
			refetchQueries: [GET_SLACK_MENTIONS],
			awaitRefetchQueries: true,
		}
	)

	// Focus navigation state for cross mention/tree keyboard nav
	const [nodeTreeFocusRequest, setNodeTreeFocusRequest] =
		useState<NodeTreeFocusRequest>(null)
	const focusSeqCounterRef = useRef(0)

	// どのメンションのツリーで node がフォーカスされているか（child → parent）。
	// node フォーカス中はメンション用ショートカット（E / 3 / 4 / j / k 等）を
	// 親で処理せず、フォーカス中のノード側（MentionNodeTree）に委ねる。
	const [nodeFocusedMentionId, setNodeFocusedMentionId] = useState<
		string | null
	>(null)
	const handleNodeFocusChange = useCallback(
		(mentionId: string, nodeId: string | null) => {
			setNodeFocusedMentionId((prev) =>
				nodeId !== null ? mentionId : prev === mentionId ? null : prev
			)
		},
		[]
	)

	const syncState = syncStateData?.taigaSlackMentionSyncState.syncState
	const rawMentions = slackMentionsData?.taigaSlackMentions.mentions ?? []

	// アクティブタブの mention が持つ session node id を集める。
	// embedded session の親 node に加え、session を持つ child node も polling 対象にする
	// (child node の status も 3 秒 polling で更新するため)。
	const polledNodeIds = useMemo(() => {
		const ids: string[] = []
		for (const mention of rawMentions) {
			const meta = getFragmentData(TaigaSlackMentionMetaFragment, mention)
			if (meta.embeddedNode) ids.push(meta.embeddedNode.id)
			for (const child of meta.childNodes) {
				if (child.session) ids.push(child.id)
			}
		}
		return ids
	}, [rawMentions])

	// session status の polling は親 (TaigaPage) で一括して行うため、
	// session を持つ node id を親へ通知する。実際の status は
	// props の sessionStatusMap で受け取る。
	const polledNodeIdsKey = polledNodeIds.join(",")
	useEffect(() => {
		onEmbeddedNodeIdsChange(polledNodeIdsKey ? polledNodeIdsKey.split(",") : [])
	}, [polledNodeIdsKey, onEmbeddedNodeIdsChange])

	// 親から受け取った polling 結果を、MentionRow が使う形 (nodeId -> status/memo) に整える。
	const polledSessionByNodeId = useMemo(() => {
		const map: Record<
			string,
			{
				sessionStatus: string
				isRemoteControlEnabled: boolean
				memo: string | null
			}
		> = {}
		for (const nodeId of polledNodeIds) {
			const polled = sessionStatusMap.get(nodeId)
			if (polled) {
				map[nodeId] = {
					sessionStatus: polled.session.sessionStatus,
					isRemoteControlEnabled: polled.session.isRemoteControlEnabled,
					memo: polled.session.memo,
				}
			}
		}
		return map
	}, [polledNodeIds, sessionStatusMap])

	// タブラベルの (N) 表示用にユーザーごとの件数を集計する。
	// 自分タブは optimistic hide 反映、他人タブは raw 件数。
	const mentionCounts = useMemo(() => {
		const counts: Record<string, number> = {}
		for (const user of MENTION_USERS) {
			const mentions =
				mentionsQueriesByUserId[user.taigaUserId].data?.taigaSlackMentions
					.mentions ?? []
			counts[user.taigaUserId] = mentions.filter((mention) => {
				const meta = getFragmentData(TaigaSlackMentionMetaFragment, mention)
				return !hiddenMentionIds.has(meta.id)
			}).length
		}
		return counts
	}, [mentionsQueriesByUserId, hiddenMentionIds])

	const getVisibleMentionsMeta = useCallback(
		(mentions: typeof rawMentions) =>
			mentions
				.map((mention) => ({
					masked: mention,
					meta: getFragmentData(TaigaSlackMentionMetaFragment, mention),
				}))
				.filter(({ meta }) => {
					if (hiddenMentionIds.has(meta.id)) return false
					const override = mentionOverrides[meta.id]
					const dueDate =
						override && "dueDate" in override ? override.dueDate : meta.dueDate
					const comment =
						override && "comment" in override ? override.comment : meta.comment
					const { hasBusy, hasIdle, hasRc } = getMentionSessionBuckets(
						meta,
						polledSessionByNodeId
					)
					return (
						matchesPriorityFilter(meta.priority, priorityFilter) &&
						matchesDueDateFilter(dueDate, dueDateFilter) &&
						matchesSenderFilter(meta.senderDisplayName, senderFilter) &&
						matchesSessionFilter(hasBusy, hasIdle, hasRc, sessionFilter) &&
						matchesSearchFilter(
							getMentionSearchTexts(meta, comment, polledSessionByNodeId),
							searchKeyword
						)
					)
				}),
		[
			hiddenMentionIds,
			mentionOverrides,
			priorityFilter,
			dueDateFilter,
			senderFilter,
			sessionFilter,
			searchKeyword,
			polledSessionByNodeId,
		]
	)

	// 親では meta fragment 経由で id 等にアクセスする (masking 維持)
	const mentionsMeta = useMemo(
		() => getVisibleMentionsMeta(rawMentions),
		[getVisibleMentionsMeta, rawMentions]
	)

	const visibleMaskedMentions = useMemo(
		() => mentionsMeta.map(({ masked }) => masked),
		[mentionsMeta]
	)

	// 各 filter button に「その選択肢にしたら何件になるか」を出すため、
	// hidden を除き override 反映済みの (meta, dueDate) リストを共通化する。
	const baseMentionsMeta = useMemo(() => {
		return rawMentions
			.map((mention) => {
				const meta = getFragmentData(TaigaSlackMentionMetaFragment, mention)
				const override = mentionOverrides[meta.id]
				const dueDate =
					override && "dueDate" in override ? override.dueDate : meta.dueDate
				const comment =
					override && "comment" in override ? override.comment : meta.comment
				return {
					meta,
					dueDate,
					searchTexts: getMentionSearchTexts(
						meta,
						comment,
						polledSessionByNodeId
					),
					...getMentionSessionBuckets(meta, polledSessionByNodeId),
				}
			})
			.filter(({ meta }) => !hiddenMentionIds.has(meta.id))
	}, [rawMentions, mentionOverrides, hiddenMentionIds, polledSessionByNodeId])

	// 優先度フィルタの各選択肢ごとの件数: 自分以外のフィルタ (期限 / 送信者 / セッション) は現状維持。
	const priorityFilterCounts = useMemo(() => {
		const filtered = baseMentionsMeta.filter(
			({ meta, dueDate, hasBusy, hasIdle, hasRc, searchTexts }) =>
				matchesDueDateFilter(dueDate, dueDateFilter) &&
				matchesSenderFilter(meta.senderDisplayName, senderFilter) &&
				matchesSessionFilter(hasBusy, hasIdle, hasRc, sessionFilter) &&
				matchesSearchFilter(searchTexts, searchKeyword)
		)
		const counts: Record<string, number> = {}
		for (const option of PRIORITY_FILTER_OPTIONS) {
			const value = option.value
			// null (全部) は他フィルタ適用後の全件数。それ以外はその選択肢単体の件数。
			counts[String(value)] =
				value === null
					? filtered.length
					: filtered.filter(({ meta }) =>
							matchesPriorityValue(meta.priority, value)
						).length
		}
		return counts
	}, [
		baseMentionsMeta,
		dueDateFilter,
		senderFilter,
		sessionFilter,
		searchKeyword,
	])

	// 期限フィルタの各選択肢ごとの件数: 自分以外のフィルタ (優先度 / 送信者 / セッション) は現状維持。
	const dueDateFilterCounts = useMemo(() => {
		const filtered = baseMentionsMeta.filter(
			({ meta, hasBusy, hasIdle, hasRc, searchTexts }) =>
				matchesPriorityFilter(meta.priority, priorityFilter) &&
				matchesSenderFilter(meta.senderDisplayName, senderFilter) &&
				matchesSessionFilter(hasBusy, hasIdle, hasRc, sessionFilter) &&
				matchesSearchFilter(searchTexts, searchKeyword)
		)
		const counts: Record<string, number> = {}
		for (const option of DUE_DATE_FILTER_OPTIONS) {
			const value = option.value
			// null (全て) は他フィルタ適用後の全件数。それ以外はその選択肢単体の件数。
			counts[String(value)] =
				value === null
					? filtered.length
					: filtered.filter(({ dueDate }) =>
							matchesDueDateValue(dueDate, value)
						).length
		}
		return counts
	}, [
		baseMentionsMeta,
		priorityFilter,
		senderFilter,
		sessionFilter,
		searchKeyword,
	])

	// セッション(ビジー/アイドル/RC)フィルタの各バケット件数: 自分以外のフィルタ
	// (優先度 / 期限 / 送信者) は現状維持。
	// - busy: BUSY 状態のセッションノードを持つ mention 数
	// - idle: BUSY 以外 (IDLE / PENDING) のセッションノードを持つ mention 数
	//   (両方のバケットに該当する mention は busy・idle の両方でカウントされる)
	// - all: RC の有無を問わない全 mention 数 (RC フィルタ「すべて」の件数)
	// - rc: Remote Control 有効のセッションノードを持つ mention 数
	// - nonRc: RC 有効セッションノードを持たない mention 数
	const sessionFilterCounts = useMemo(() => {
		const filtered = baseMentionsMeta.filter(
			({ meta, dueDate, searchTexts }) =>
				matchesPriorityFilter(meta.priority, priorityFilter) &&
				matchesDueDateFilter(dueDate, dueDateFilter) &&
				matchesSenderFilter(meta.senderDisplayName, senderFilter) &&
				matchesSearchFilter(searchTexts, searchKeyword)
		)
		const rcCount = filtered.filter(({ hasRc }) => hasRc).length
		return {
			busy: filtered.filter(({ hasBusy }) => hasBusy).length,
			idle: filtered.filter(({ hasIdle }) => hasIdle).length,
			all: filtered.length,
			rc: rcCount,
			nonRc: filtered.length - rcCount,
		}
	}, [
		baseMentionsMeta,
		priorityFilter,
		dueDateFilter,
		senderFilter,
		searchKeyword,
	])

	// 送信者フィルタの選択肢は、アクティブタブの未対応メンション (hidden を除く) から
	// senderDisplayName をユニーク化して、日本語ロケールで五十音順にソートする。
	// 他フィルタの選択状態には影響されない (ユーザーがフィルタを切り替えやすくするため)。
	const senderOptions = useMemo(() => {
		const senders = new Set<string>()
		for (const mention of rawMentions) {
			const meta = getFragmentData(TaigaSlackMentionMetaFragment, mention)
			if (hiddenMentionIds.has(meta.id)) continue
			if (meta.senderDisplayName) senders.add(meta.senderDisplayName)
		}
		return Array.from(senders).sort((a, b) => a.localeCompare(b, "ja"))
	}, [rawMentions, hiddenMentionIds])

	// 送信者フィルタの各送信者ごとの件数: 自分以外のフィルタ (優先度 / 期限 / セッション / 検索) は現状維持。
	const senderFilterCounts = useMemo(() => {
		const filtered = baseMentionsMeta.filter(
			({ meta, dueDate, hasBusy, hasIdle, hasRc, searchTexts }) =>
				matchesPriorityFilter(meta.priority, priorityFilter) &&
				matchesDueDateFilter(dueDate, dueDateFilter) &&
				matchesSessionFilter(hasBusy, hasIdle, hasRc, sessionFilter) &&
				matchesSearchFilter(searchTexts, searchKeyword)
		)
		const counts: Record<string, number> = {}
		for (const sender of senderOptions) {
			counts[sender] = filtered.filter(
				({ meta }) => meta.senderDisplayName === sender
			).length
		}
		return counts
	}, [
		baseMentionsMeta,
		senderOptions,
		priorityFilter,
		dueDateFilter,
		sessionFilter,
		searchKeyword,
	])

	// 送信者フィルタは複数選択。null = 全員選択 (デフォルト) で、その状態から 1 人押したときは
	// 「全員選択から その 1 人を外す」挙動にする (見た目上すべて選択済みのため)。
	const handleToggleSenderFilter = useCallback(
		(sender: string) => {
			setSenderFilter((current) => {
				const selected = current ?? senderOptions
				return selected.includes(sender)
					? selected.filter((v) => v !== sender)
					: [...selected, sender]
			})
		},
		[senderOptions]
	)
	// 「すべて」= 全員選択に戻す (以後に増えた送信者も自動で含める)。
	const handleSelectAllSenders = useCallback(() => setSenderFilter(null), [])
	// 「クリア」= 選択を全部外す (0 件表示。ここから 1 人ずつ足していける)。
	const handleClearSenderFilter = useCallback(() => setSenderFilter([]), [])

	const focusedMentionMeta = useMemo(
		() => mentionsMeta.find(({ meta }) => meta.id === focusedMentionId)?.meta,
		[focusedMentionId, mentionsMeta]
	)

	const focusMentionByOffset = useCallback(
		(offset: number) => {
			if (mentionsMeta.length === 0) return
			const currentIndex = mentionsMeta.findIndex(
				({ meta }) => meta.id === focusedMentionId
			)
			if (currentIndex === -1) {
				setFocusedMentionId(mentionsMeta[0].meta.id)
				return
			}
			const nextIndex = Math.min(
				mentionsMeta.length - 1,
				Math.max(0, currentIndex + offset)
			)
			setFocusedMentionId(mentionsMeta[nextIndex].meta.id)
		},
		[focusedMentionId, mentionsMeta]
	)

	// スレッドの親 ts から、スレッド内で最も新しいメッセージの ts を取得する。
	// 取得に失敗した場合や reply が無い場合は親 ts をそのまま返す。
	const resolveLatestReplyTs = useCallback(
		async (channelId: string, threadTs: string): Promise<string> => {
			try {
				const { data } = await apolloClient.query({
					query: GET_SLACK_LATEST_REPLY_TS,
					variables: { param: { channelId, threadTs } },
					fetchPolicy: "no-cache",
				})
				return data?.slackLatestReplyTs?.ts ?? threadTs
			} catch {
				return threadTs
			}
		},
		[apolloClient]
	)

	const openSlackMentionByMeta = useCallback(
		(meta: {
			channelId: string
			threadTs: string | null
			messageTs: string
			permalink: string | null
		}) => {
			const threadTs = meta.threadTs ?? meta.messageTs
			// mention メッセージの permalink ではなく、スレッド最新メッセージを指す URL を
			// 組み立てて開く。ブラウザ・モバイル(アプリ)・deep link のどの経路でも
			// 最新メッセージにフォーカスされ、挙動が揃う。
			void (async () => {
				const latestTs = await resolveLatestReplyTs(meta.channelId, threadTs)
				const url = buildSlackFocusUrl(
					meta.channelId,
					threadTs,
					latestTs,
					isMobile
				)
				openSlackThread(
					url,
					meta.channelId,
					threadTs,
					latestTs,
					slackAppSetting,
					isMobile
				)
			})()
		},
		[slackAppSetting, resolveLatestReplyTs, isMobile]
	)

	const handleOpenSlack = useCallback(
		(id: string) => {
			const meta = mentionsMeta.find(({ meta }) => meta.id === id)?.meta
			if (!meta) return
			openSlackMentionByMeta(meta)
		},
		[mentionsMeta, openSlackMentionByMeta]
	)

	const restoreMentionOverride = useCallback(
		(id: string, previous?: MentionOverride) => {
			setMentionOverrides((current) => {
				const next = { ...current }
				if (previous) {
					next[id] = previous
				} else {
					delete next[id]
				}
				return next
			})
		},
		[]
	)

	// completeEmbeddedSession=false: mention のみ完了 (Q / 完了)。
	// completeEmbeddedSession=true: 同一スレッドの embed session も完了し :done: を投稿 (全完了)。
	const handleCompleteMention = useCallback(
		(id: string, completeEmbeddedSession: boolean) => {
			const index = mentionsMeta.findIndex(({ meta }) => meta.id === id)
			const nextFocusedMeta =
				mentionsMeta[index + 1]?.meta ?? mentionsMeta[index - 1]?.meta ?? null

			setHiddenMentionIds((current) => {
				const next = new Set(current)
				next.add(id)
				return next
			})
			if (focusedMentionId === id) {
				setFocusedMentionId(nextFocusedMeta?.id ?? null)
			}
			if (editingMentionId === id) {
				setEditingMentionId(null)
				setEditingComment("")
			}

			void completeSlackMention({
				variables: { input: { id, completeEmbeddedSession } },
			}).catch((error: unknown) => {
				setHiddenMentionIds((current) => {
					const next = new Set(current)
					next.delete(id)
					return next
				})
				setFocusedMentionId(id)
				appAlert(`完了に失敗しました: ${getErrorMessage(error)}`)
			})
		},
		[completeSlackMention, editingMentionId, focusedMentionId, mentionsMeta]
	)

	// 完了 (Q): mention のみ完了する。embed session には触れない。
	const handleCompleteMentionOnly = useCallback(
		(id: string) => handleCompleteMention(id, false),
		[handleCompleteMention]
	)
	// 全完了: mention + embed session を完了する。
	const handleCompleteMentionAll = useCallback(
		(id: string) => handleCompleteMention(id, true),
		[handleCompleteMention]
	)

	// 期限ボタン (shortcut 5): モーダルを開く。実際の確定は handleSubmitDueDate で行う。
	const handleSetDueDate = useCallback((id: string) => {
		setDueDateModalMentionId(id)
	}, [])

	const handleCloseDueDateModal = useCallback(() => {
		setDueDateModalMentionId(null)
	}, [])

	// モーダルで選択された日数で期限を確定する (optimistic update + mutation)。
	const handleSubmitDueDate = useCallback(
		(id: string, daysFromToday: number) => {
			setDueDateModalMentionId(null)

			// 期限を確定したら、対象 mention の「1 個下 → 無ければ 1 個上」へ
			// フォーカスを移して次の作業に進めるようにする (priority 操作と同じ挙動)。
			setFocusedMentionId((current) => {
				if (current !== id) return current
				const index = mentionsMeta.findIndex(({ meta }) => meta.id === id)
				if (index === -1) return current
				return (
					mentionsMeta[index + 1]?.meta.id ??
					mentionsMeta[index - 1]?.meta.id ??
					current
				)
			})

			// optimistic: 今日 + daysFromToday を YYYY-MM-DD (JST) で計算
			const previousOverride = mentionOverrides[id]
			const optimisticDate = new Date(
				Date.now() + daysFromToday * 24 * 60 * 60 * 1000
			)
			const optimisticDueDate = formatJstDateUntilDayByHyphen(optimisticDate)
			setMentionOverrides((current) => ({
				...current,
				[id]: {
					...(current[id] ?? {}),
					dueDate: optimisticDueDate,
				},
			}))

			void setSlackMentionDueDate({
				variables: { input: { id, daysFromToday } },
			})
				.then((result) => {
					const updated = result.data?.setTaigaSlackMentionDueDate.mention
					if (updated) {
						setMentionOverrides((current) => ({
							...current,
							[id]: {
								...(current[id] ?? {}),
								dueDate: updated.dueDate,
							},
						}))
					}
				})
				.catch((error: unknown) => {
					restoreMentionOverride(id, previousOverride)
					appAlert(`期限設定に失敗しました: ${getErrorMessage(error)}`)
				})
		},
		[
			setSlackMentionDueDate,
			mentionOverrides,
			restoreMentionOverride,
			mentionsMeta,
		]
	)

	// 明日ボタン (shortcut 2): モーダルを開かず、即座に期限を明日 (1日後) に設定する。
	const handleSetTomorrow = useCallback(
		(id: string) => {
			handleSubmitDueDate(id, 1)
		},
		[handleSubmitDueDate]
	)

	const getPriorityFocusInfo = useCallback(
		(id: string) => {
			const index = mentionsMeta.findIndex(({ meta }) => meta.id === id)
			if (index === -1) return null
			return {
				id,
				index,
				fallbackId:
					mentionsMeta[index + 1]?.meta.id ??
					mentionsMeta[index - 1]?.meta.id ??
					null,
			}
		},
		[mentionsMeta]
	)

	const getPriorityFallbackMentionId = useCallback(
		(
			focusInfo: PriorityFocusInfo | null,
			nextMentionsMeta: ReturnType<typeof getVisibleMentionsMeta>
		) => {
			if (!focusInfo) return nextMentionsMeta[0]?.meta.id ?? null

			const nextIndex = nextMentionsMeta.findIndex(
				({ meta }) => meta.id === focusInfo.id
			)
			if (nextIndex === focusInfo.index) {
				return focusInfo.id
			}
			const fallbackMeta = focusInfo.fallbackId
				? nextMentionsMeta.find(({ meta }) => meta.id === focusInfo.fallbackId)
						?.meta
				: null
			const sameIndexMeta =
				focusInfo.index >= 0 ? nextMentionsMeta[focusInfo.index]?.meta : null
			return (
				fallbackMeta?.id ??
				sameIndexMeta?.id ??
				nextMentionsMeta[0]?.meta.id ??
				null
			)
		},
		[]
	)

	const handlePriorityUpMention = useCallback(
		(id: string) => {
			const focusInfo = getPriorityFocusInfo(id)
			pendingPriorityFocusRef.current = focusInfo
			void (async () => {
				try {
					await priorityUpSlackMention({
						variables: { input: { id } },
					})
					const result = await refetchSlackMentions?.()
					const nextMentionsMeta = getVisibleMentionsMeta(
						result?.data?.taigaSlackMentions.mentions ?? []
					)
					setFocusedMentionId((current) => {
						if (current !== id) return current
						return getPriorityFallbackMentionId(focusInfo, nextMentionsMeta)
					})
				} catch (error: unknown) {
					appAlert(`優先度の更新に失敗しました: ${getErrorMessage(error)}`)
				} finally {
					if (pendingPriorityFocusRef.current === focusInfo) {
						pendingPriorityFocusRef.current = null
					}
				}
			})()
		},
		[
			getPriorityFocusInfo,
			getPriorityFallbackMentionId,
			getVisibleMentionsMeta,
			priorityUpSlackMention,
			refetchSlackMentions,
		]
	)

	const handlePriorityDownMention = useCallback(
		(id: string) => {
			const focusInfo = getPriorityFocusInfo(id)
			pendingPriorityFocusRef.current = focusInfo
			void (async () => {
				try {
					await priorityDownSlackMention({
						variables: { input: { id } },
					})
					const result = await refetchSlackMentions?.()
					const nextMentionsMeta = getVisibleMentionsMeta(
						result?.data?.taigaSlackMentions.mentions ?? []
					)
					setFocusedMentionId((current) => {
						if (current !== id) return current
						return getPriorityFallbackMentionId(focusInfo, nextMentionsMeta)
					})
				} catch (error: unknown) {
					appAlert(`優先度の更新に失敗しました: ${getErrorMessage(error)}`)
				} finally {
					if (pendingPriorityFocusRef.current === focusInfo) {
						pendingPriorityFocusRef.current = null
					}
				}
			})()
		},
		[
			getPriorityFocusInfo,
			getPriorityFallbackMentionId,
			getVisibleMentionsMeta,
			priorityDownSlackMention,
			refetchSlackMentions,
		]
	)

	const handleStartEditComment = useCallback(
		(id: string) => {
			const meta = mentionsMeta.find(({ meta }) => meta.id === id)?.meta
			const override = mentionOverrides[id]
			const currentComment =
				override && "comment" in override
					? (override.comment ?? "")
					: (meta?.comment ?? "")
			setFocusedMentionId(id)
			setEditingMentionId(id)
			setEditingComment(currentComment)
		},
		[mentionsMeta, mentionOverrides]
	)

	const handleCancelEditComment = useCallback(() => {
		setEditingMentionId(null)
		setEditingComment("")
	}, [])

	const handleSyncMentions = useCallback(() => {
		void syncSlackMentions({ variables: { input: {} } }).catch(
			(error: unknown) => {
				appAlert(`同期に失敗しました: ${getErrorMessage(error)}`)
			}
		)
	}, [syncSlackMentions])

	const handleOpenCreateModal = useCallback(() => {
		setIsCreateModalOpen(true)
	}, [])

	const handleCloseCreateModal = useCallback(() => {
		setIsCreateModalOpen(false)
	}, [])

	// 手動作成モーダルのデフォルト優先度: 優先度フィルタが1つだけ選択されていればそれに合わせる。
	// 未選択/複数選択 → 2 (通常), "3plus" → 3, 数値 → そのまま
	const createMentionDefaultPriority = useMemo(() => {
		if (priorityFilter.length !== 1) return 2
		const only = priorityFilter[0]
		if (only === "3plus") return 3
		return only
	}, [priorityFilter])

	const handleCreateMention = useCallback(
		(text: string, priority: number, mentionMaki: boolean) => {
			void (async () => {
				try {
					await createSlackMention({
						variables: { input: { text, priority, mentionMaki } },
					})
					setIsCreateModalOpen(false)
					await refetchSlackMentions?.()
				} catch (error: unknown) {
					appAlert(`メンションの作成に失敗しました: ${getErrorMessage(error)}`)
				}
			})()
		},
		[createSlackMention, refetchSlackMentions]
	)

	const handleSaveComment = useCallback(
		(id: string, comment: string) => {
			const previousOverride = mentionOverrides[id]
			setMentionOverrides((current) => ({
				...current,
				[id]: {
					...(current[id] ?? {}),
					comment,
				},
			}))
			setEditingMentionId(null)
			setEditingComment("")

			void updateSlackMentionComment({
				variables: { input: { id, comment } },
			})
				.then((result) => {
					const updated = result.data?.updateTaigaSlackMentionComment.mention
					if (updated) {
						setMentionOverrides((current) => ({
							...current,
							[id]: {
								...(current[id] ?? {}),
								comment: updated.comment,
							},
						}))
					}
				})
				.catch((error: unknown) => {
					restoreMentionOverride(id, previousOverride)
					setEditingMentionId(id)
					setEditingComment(comment)
					appAlert(`メモの保存に失敗しました: ${getErrorMessage(error)}`)
				})
		},
		[mentionOverrides, restoreMentionOverride, updateSlackMentionComment]
	)

	// Seq3: node ツリー関連ハンドラ
	const handleToggleChildNodesCollapsed = useCallback(
		(mentionId: string, currentIsCollapsed?: boolean) => {
			void toggleChildNodesCollapsed({
				variables: { input: { id: mentionId } },
				optimisticResponse:
					currentIsCollapsed !== undefined
						? {
								toggleTaigaSlackMentionChildNodesCollapsed: {
									mention: {
										id: mentionId,
										isChildNodesCollapsed: !currentIsCollapsed,
									},
								},
							}
						: undefined,
			}).catch((error: unknown) => {
				appAlert(`ノードツリーの開閉に失敗しました: ${getErrorMessage(error)}`)
			})
		},
		[toggleChildNodesCollapsed]
	)

	// 「+ node を追加」: セッション作成モーダル (node 新規作成モード) を開く。
	const handleAddRootNode = useCallback((mentionId: string) => {
		setNodeSessionModal({ kind: "newRootNode", mentionId })
	}, [])

	// 「embed 引き継ぎ」ボタン: 対象 mention を覚えて URL 入力モーダルを開く。
	const handleOpenHandOver = useCallback((mentionId: string) => {
		setHandOverMentionId(mentionId)
	}, [])

	// モーダルで URL を確定 → その embed session を対象 mention の root node に引き継ぐ。
	const handleSubmitHandOver = useCallback(
		(threadTs: string, channelId: string) => {
			if (!handOverMentionId) return
			void handOverEmbedSession({
				variables: {
					input: { slackMentionId: handOverMentionId, threadTs, channelId },
				},
			})
				.then(() => setHandOverMentionId(null))
				.catch((error: unknown) => {
					appAlert(`引き継ぎに失敗しました: ${getErrorMessage(error)}`)
				})
		},
		[handOverEmbedSession, handOverMentionId]
	)

	const handleUpdateNode = useCallback(
		(id: string, name: string, isCollapsed?: boolean) => {
			void updateTaigaNode({
				variables: {
					input: {
						id,
						name,
						...(isCollapsed !== undefined ? { isCollapsed } : {}),
					},
				},
			}).catch((error: unknown) => {
				appAlert(`ノードの更新に失敗しました: ${getErrorMessage(error)}`)
			})
		},
		[updateTaigaNode]
	)

	const handleArchiveNode = useCallback(
		(id: string) => {
			void archiveTaigaNode({
				variables: { input: { id } },
			}).catch((error: unknown) => {
				appAlert(`ノードのアーカイブに失敗しました: ${getErrorMessage(error)}`)
			})
		},
		[archiveTaigaNode]
	)

	// 子ノード追加: セッション作成モーダル (node 新規作成モード) を開く。
	const handleAddChildNode = useCallback(
		(milestoneId: string | null, parentNodeId: string) => {
			setNodeSessionModal({ kind: "newChildNode", milestoneId, parentNodeId })
		},
		[]
	)

	// モーダルの kind に応じてノード作成 mutation を実行し、作成された node id を返す。
	const createNodeFromModal = useCallback(
		async (
			modal:
				| { kind: "newRootNode"; mentionId: string }
				| {
						kind: "newChildNode"
						milestoneId: string | null
						parentNodeId: string
				  },
			prompt: string
		): Promise<string | undefined> => {
			if (modal.kind === "newRootNode") {
				const result = await createNodeUnderSlackMention({
					variables: {
						input: { slackMentionId: modal.mentionId, prompt },
					},
				})
				return result.data?.createTaigaNodeUnderSlackMention.node.id
			}
			const result = await createTaigaNode({
				variables: {
					input: {
						milestoneId: modal.milestoneId ?? "",
						parentNodeId: modal.parentNodeId,
						prompt,
					},
				},
			})
			return result.data?.createTaigaNode.node.id
		},
		[createNodeUnderSlackMention, createTaigaNode]
	)

	// 「ノードで作成」: session を作らずノードのみ作成する。
	const handleCreateNodeOnly = useCallback(
		(prompt: string) => {
			if (!nodeSessionModal || nodeSessionModal.kind === "existingNode") return
			void createNodeFromModal(nodeSessionModal, prompt)
				.then(() => {
					setNodeSessionModal(null)
				})
				.catch((error: unknown) => {
					appAlert(`ノードの作成に失敗しました: ${getErrorMessage(error)}`)
				})
		},
		[createNodeFromModal, nodeSessionModal]
	)

	// 「Create Session」(node 新規作成モード): ノード作成 → その node に session 作成。
	const handleCreateNodeWithSession = useCallback(
		(prompt: string, sessionPrompt: string) => {
			if (!nodeSessionModal || nodeSessionModal.kind === "existingNode") return
			void createNodeFromModal(nodeSessionModal, prompt)
				.then(async (nodeId) => {
					if (!nodeId) {
						throw new Error("作成されたノードの ID を取得できませんでした")
					}
					// session 作成に失敗してもノードは作成済みなので、モーダルを閉じて
					// 二重ノード作成を防ぐ (session はノードの S から再作成できる)。
					setNodeSessionModal(null)
					await createTaigaSession({
						variables: { input: { nodeId, prompt: sessionPrompt } },
					}).catch((error: unknown) => {
						appAlert(`Session の作成に失敗しました: ${getErrorMessage(error)}`)
					})
				})
				.catch((error: unknown) => {
					appAlert(`ノードの作成に失敗しました: ${getErrorMessage(error)}`)
				})
		},
		[createNodeFromModal, createTaigaSession, nodeSessionModal]
	)

	// Seq3: node ツリー操作ハンドラ (TaigaSheetDetailPage と同等)
	const handleNodePriorityUp = useCallback(
		(nodeId: string) => {
			void priorityUpTaigaNode({
				variables: { input: { nodeId } },
			}).catch((error: unknown) => {
				appAlert(`Priority の更新に失敗しました: ${getErrorMessage(error)}`)
			})
		},
		[priorityUpTaigaNode]
	)

	const handleNodePriorityDown = useCallback(
		(nodeId: string) => {
			void priorityDownTaigaNode({
				variables: { input: { nodeId } },
			}).catch((error: unknown) => {
				appAlert(`Priority の更新に失敗しました: ${getErrorMessage(error)}`)
			})
		},
		[priorityDownTaigaNode]
	)

	const handleNodeCreateSession = useCallback(
		(nodeId: string, nodeName: string) => {
			setNodeSessionModal({ kind: "existingNode", nodeId, nodeName })
		},
		[]
	)

	const handleNodeSessionSubmit = useCallback(
		(nodeId: string, prompt: string) => {
			void createTaigaSession({
				variables: { input: { nodeId, prompt } },
			})
				.then(() => {
					setNodeSessionModal(null)
				})
				.catch((error: unknown) => {
					appAlert(`Session の作成に失敗しました: ${getErrorMessage(error)}`)
				})
		},
		[createTaigaSession]
	)

	const handleNodeAttachSlackThread = useCallback(
		(
			nodeId: string,
			threadTs: string,
			channelId: string,
			memo: string | null
		) => {
			void attachSlackThread({
				variables: { input: { nodeId, threadTs, channelId, memo } },
			})
				.then(() => {
					setNodeSessionModal(null)
				})
				.catch((error: unknown) => {
					appAlert(
						`Slack スレッドの紐付けに失敗しました: ${getErrorMessage(error)}`
					)
				})
		},
		[attachSlackThread]
	)

	const handleNodeCompleteSession = useCallback(
		async (nodeId: string, nodeName: string, hasSession: boolean) => {
			if (!(await appConfirm("このノードを完了にしますか？"))) return

			// session があるノードは completeTaigaSession (Slack に :done: リアクション)。
			// session が無いノードは updateTaigaNode で isDone = true にする。
			if (!hasSession) {
				void updateTaigaNode({
					variables: {
						input: { id: nodeId, name: nodeName, isDone: true },
					},
				}).catch((error: unknown) => {
					appAlert(`完了に失敗しました: ${getErrorMessage(error)}`)
				})
				return
			}

			void completeTaigaSession({
				variables: { input: { nodeId } },
			}).catch((error: unknown) => {
				appAlert(`完了に失敗しました: ${getErrorMessage(error)}`)
			})
		},
		[completeTaigaSession, updateTaigaNode]
	)

	const handleNodeForkSession = useCallback(
		async (nodeId: string, nodeName: string) => {
			if (
				!(await appConfirm(
					`「${nodeName}」のセッションをフォークしますか？\n同じ階層に worktree・会話履歴を引き継いだ複製セッションを作成します。`
				))
			)
				return

			void forkTaigaSession({
				variables: { input: { nodeId } },
			}).catch((error: unknown) => {
				appAlert(`フォークに失敗しました: ${getErrorMessage(error)}`)
			})
		},
		[forkTaigaSession]
	)

	// embed session の Fork: フォーク先ノードは元ノードの階層ではなく、この mention 直下の
	// nodes (root node) に作る（embed session の node は orphan / adhoc 配下にあり得るため）。
	const handleEmbeddedSessionFork = useCallback(
		async (mentionId: string, nodeId: string, nodeName: string) => {
			if (
				!(await appConfirm(
					`embed session「${nodeName}」をフォークしますか？\nこの mention 直下の nodes に worktree・会話履歴を引き継いだ複製セッションを作成します。`
				))
			)
				return

			void forkTaigaSession({
				variables: { input: { nodeId, slackMentionId: mentionId } },
			}).catch((error: unknown) => {
				appAlert(`フォークに失敗しました: ${getErrorMessage(error)}`)
			})
		},
		[forkTaigaSession]
	)

	const handleNodePendSession = useCallback(
		(nodeId: string) => {
			void updateTaigaSessionStatus({
				variables: { input: { nodeId, status: "PENDING" } },
			}).catch((error: unknown) => {
				appAlert(`Pending への変更に失敗しました: ${getErrorMessage(error)}`)
			})
		},
		[updateTaigaSessionStatus]
	)

	const handleNodeOpenSlack = useCallback(
		(nodeId: string, threadTs: string, channelId: string) => {
			void (async () => {
				let focusTs = threadTs
				try {
					const { data } = await apolloClient.query({
						query: GET_LATEST_THREAD_TS_FOR_MENTION,
						variables: { param: { nodeId } },
						fetchPolicy: "no-cache",
					})
					focusTs = data?.taigaLatestThreadTs?.ts ?? threadTs
				} catch {
					// fallback: use original threadTs
				}
				const url = buildSlackFocusUrl(channelId, threadTs, focusTs, isMobile)
				// deep link でもスレッドの最新メッセージ(focusTs)へ飛ばす。
				// thread_ts=親, message=最新 をブラウザ URL と揃える。
				openSlackThread(
					url,
					channelId,
					threadTs,
					focusTs,
					slackAppSetting,
					isMobile
				)
			})()
		},
		[apolloClient, slackAppSetting, isMobile]
	)

	// 埋め込み session の Slack ボタン。ブラウザ・モバイル・deep link の
	// どの経路でもスレッド最新メッセージへ飛ばす。
	const handleEmbeddedSessionOpenSlack = useCallback(
		(channelId: string, threadTs: string) => {
			void (async () => {
				const latestTs = await resolveLatestReplyTs(channelId, threadTs)
				const url = buildSlackFocusUrl(channelId, threadTs, latestTs, isMobile)
				openSlackThread(
					url,
					channelId,
					threadTs,
					latestTs,
					slackAppSetting,
					isMobile
				)
			})()
		},
		[slackAppSetting, resolveLatestReplyTs, isMobile]
	)

	const handleNodeCopyBranch = useCallback(
		(nodeId: string) => {
			apolloClient
				.query({
					query: GET_SESSION_BRANCH_NAME_FOR_MENTION,
					variables: { param: { nodeId } },
					fetchPolicy: "no-cache",
				})
				.then(({ data }) => {
					const branchName = data?.taigaSessionBranchName?.branchName
					if (branchName === null || branchName === undefined) {
						appAlert("Branch 名のメッセージが見つかりませんでした")
						return
					}
					copyTextToClipboard(branchName).catch(() => {
						appAlert("Branch 名のコピーに失敗しました")
					})
				})
				.catch(() => {
					appAlert("Branch 名の取得に失敗しました")
				})
		},
		[apolloClient]
	)

	const handleNodeOpenMemo = useCallback((node: SharedNode) => {
		setNodeMemoModal(node)
	}, [])

	const handleNodeMemoSubmit = useCallback(
		(nodeId: string, memo: string | null) => {
			void updateTaigaSessionMemo({
				variables: { input: { nodeId, memo } },
			})
				.then(() => {
					setNodeMemoModal(null)
				})
				.catch((error: unknown) => {
					appAlert(`メモの保存に失敗しました: ${getErrorMessage(error)}`)
				})
		},
		[updateTaigaSessionMemo]
	)

	const handleNodeMove = useCallback(
		(
			nodeId: string,
			targetParentNodeId: string | null,
			afterNodeId: string | null
		) => {
			void moveTaigaNode({
				variables: {
					input: {
						nodeId,
						targetParentNodeId,
						targetMilestoneId: "",
						afterNodeId,
					},
				},
			}).catch((error: unknown) => {
				appAlert(`ノードの移動に失敗しました: ${getErrorMessage(error)}`)
			})
		},
		[moveTaigaNode]
	)

	const handleNodeReorder = useCallback(
		(parentNodeId: string | null, nodeIds: string[]) => {
			void reorderTaigaNodes({
				variables: {
					input: {
						parentNodeId,
						nodeIds,
					},
				},
			}).catch((error: unknown) => {
				appAlert(`ノードの並び替えに失敗しました: ${getErrorMessage(error)}`)
			})
		},
		[reorderTaigaNodes]
	)

	// Build a map of mentionId → isTreeOpen for focus navigation.
	// Uses SlackMentionsSectionMentionFragment which directly owns isChildNodesCollapsed and childNodesCount.
	const mentionTreeOpenMap = useMemo(() => {
		const map: Record<string, boolean> = {}
		for (const { masked } of mentionsMeta) {
			const sectionData = getFragmentData(
				SlackMentionsSectionMentionFragment,
				masked
			)
			// Tree is open when: not collapsed AND has child nodes (for own tab)
			// For read-only tab, tree is locally managed so we can't see it here - skip focus nav into trees
			map[sectionData.id] =
				!isReadOnlyMentionTab &&
				!sectionData.isChildNodesCollapsed &&
				sectionData.childNodesCount > 0
		}
		return map
	}, [mentionsMeta, isReadOnlyMentionTab])

	// Focus escape from tree bottom → move focus to next mention
	const handleNodeFocusEscapeDown = useCallback(
		(mentionId: string) => {
			const currentIndex = mentionsMeta.findIndex(
				({ meta }) => meta.id === mentionId
			)
			if (currentIndex === -1) return
			const nextIndex = currentIndex + 1
			if (nextIndex < mentionsMeta.length) {
				setFocusedMentionId(mentionsMeta[nextIndex].meta.id)
			}
			// else: already at last mention, do nothing
		},
		[mentionsMeta]
	)

	// Focus escape from tree top → move focus back to parent mention
	const handleNodeFocusEscapeUp = useCallback((mentionId: string) => {
		setFocusedMentionId(mentionId)
	}, [])

	useEffect(() => {
		const currentIds = mentionsMeta.map(({ meta }) => meta.id)
		const previousIds = previousVisibleIdsRef.current
		previousVisibleIdsRef.current = currentIds

		if (currentIds.length === 0) {
			setFocusedMentionId(null)
			return
		}
		if (!focusedMentionId) {
			setFocusedMentionId(currentIds[0])
			return
		}
		if (currentIds.includes(focusedMentionId)) return

		// フォーカス中の mention が一覧から消えた場合のフォールバック。
		// priority 操作中はその専用ロジックを使う。それ以外 (期限変更で
		// フィルタから外れた等) は、消える直前の並びを基準に「1 個下 → 無ければ
		// 1 個上」へフォーカスを移す。一番上へ飛ばさない。
		const pendingPriorityFocus = pendingPriorityFocusRef.current
		if (pendingPriorityFocus?.id === focusedMentionId) {
			setFocusedMentionId(
				getPriorityFallbackMentionId(pendingPriorityFocus, mentionsMeta)
			)
			return
		}
		const adjacentId = getAdjacentVisibleMentionId(
			previousIds,
			focusedMentionId,
			currentIds
		)
		setFocusedMentionId(adjacentId ?? currentIds[0])
	}, [focusedMentionId, getPriorityFallbackMentionId, mentionsMeta])

	// アクティブユーザーが変わったら focus / edit 状態をリセットする。
	useEffect(() => {
		setFocusedMentionId(null)
		setEditingMentionId(null)
		setEditingComment("")
	}, [effectiveActiveUserId])

	useEffect(() => {
		if (!isExpanded) return

		const handleKeyDown = (event: KeyboardEvent) => {
			if (editingMentionId) return
			// 期限設定モーダルを開いている間はモーダル側がキー操作を処理する。
			if (dueDateModalMentionId !== null) return
			if (isEditableElement(event.target)) return
			// node ツリー内のノードがフォーカスされている間は、メンション用ショートカット
			// （j/k の移動・e のコメント編集・3/4 の priority 等）を一切処理しない。
			// → フォーカス中のノード側（MentionNodeTree）がノード用ショートカットとして処理する。
			if (nodeFocusedMentionId !== null) return

			if (event.key === "j" || event.key === "ArrowDown") {
				event.preventDefault()
				if (focusedMentionId && mentionTreeOpenMap[focusedMentionId]) {
					// Current mention's tree is open → enter tree from top
					focusSeqCounterRef.current += 1
					setNodeTreeFocusRequest({
						mentionId: focusedMentionId,
						direction: "top",
						seq: focusSeqCounterRef.current,
					})
				} else {
					focusMentionByOffset(1)
				}
				return
			}
			if (event.key === "k" || event.key === "ArrowUp") {
				event.preventDefault()
				// Check if previous mention's tree is open → enter that tree from bottom
				const currentIndex = mentionsMeta.findIndex(
					({ meta }) => meta.id === focusedMentionId
				)
				const prevMentionId =
					currentIndex > 0 ? mentionsMeta[currentIndex - 1].meta.id : null
				if (prevMentionId && mentionTreeOpenMap[prevMentionId]) {
					focusSeqCounterRef.current += 1
					setNodeTreeFocusRequest({
						mentionId: prevMentionId,
						direction: "bottom",
						seq: focusSeqCounterRef.current,
					})
					setFocusedMentionId(prevMentionId)
				} else {
					focusMentionByOffset(-1)
				}
				return
			}
			if (!focusedMentionMeta) return

			if (event.key === "1") {
				event.preventDefault()
				openSlackMentionByMeta(focusedMentionMeta)
				return
			}
			// 他人タブでは read-only。書き込み系ショートカットは無視する。
			if (isReadOnlyMentionTab) return
			if (event.key === "2") {
				event.preventDefault()
				handleSetTomorrow(focusedMentionMeta.id)
				return
			}
			if (event.key === "3") {
				event.preventDefault()
				handlePriorityUpMention(focusedMentionMeta.id)
				return
			}
			if (event.key === "4") {
				event.preventDefault()
				handlePriorityDownMention(focusedMentionMeta.id)
				return
			}
			if (event.key === "5") {
				event.preventDefault()
				handleSetDueDate(focusedMentionMeta.id)
				return
			}
			if (event.key.toLowerCase() === "e") {
				event.preventDefault()
				handleStartEditComment(focusedMentionMeta.id)
				return
			}
			if (event.key.toLowerCase() === "q") {
				event.preventDefault()
				handleCompleteMentionOnly(focusedMentionMeta.id)
				return
			}
		}

		window.addEventListener("keydown", handleKeyDown)
		return () => window.removeEventListener("keydown", handleKeyDown)
	}, [
		isExpanded,
		isReadOnlyMentionTab,
		editingMentionId,
		dueDateModalMentionId,
		nodeFocusedMentionId,
		focusMentionByOffset,
		focusedMentionId,
		focusedMentionMeta,
		mentionsMeta,
		mentionTreeOpenMap,
		handleCompleteMentionOnly,
		handleSetDueDate,
		handleSetTomorrow,
		handlePriorityDownMention,
		handlePriorityUpMention,
		handleStartEditComment,
		openSlackMentionByMeta,
	])

	return (
		<section className={styles.section}>
			<header
				className={cx(
					styles.sectionHeader,
					!isExpanded && styles.sectionHeaderCollapsed
				)}
				onClick={onToggleExpanded}
				role="button"
				tabIndex={0}
				aria-expanded={isExpanded}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault()
						onToggleExpanded()
					}
				}}
			>
				<span
					className={cx(
						styles.sectionToggle,
						!isExpanded && styles.sectionToggleCollapsed
					)}
					aria-hidden
				>
					▾
				</span>
				<h2 className={styles.sectionTitle}>Taiga Slack Mentions</h2>
			</header>
			{isExpanded && (
				<div className={styles.sectionBody}>
					<div
						className={cx(styles.tabs, styles.tabsScroll)}
						role="tablist"
						aria-label="Taiga mentions users"
					>
						{MENTION_USERS.map((user) => {
							const count = mentionCounts[user.taigaUserId] ?? 0
							const isTabActive = user.taigaUserId === effectiveActiveUserId
							return (
								<button
									key={user.taigaUserId}
									type="button"
									role="tab"
									aria-selected={isTabActive}
									className={cx(
										styles.tabButton,
										isTabActive && styles.tabButtonActive
									)}
									onClick={() => onActiveUserIdChange(user.taigaUserId)}
								>
									{user.label} ({count})
								</button>
							)
						})}
					</div>
					<SlackMentionsSection
						mentions={visibleMaskedMentions}
						polledSessionByNodeId={polledSessionByNodeId}
						syncState={syncState}
						loading={slackMentionsLoading && !slackMentionsData}
						error={slackMentionsError ?? syncStateError}
						syncStateLoading={syncStateLoading && !syncState}
						isSyncing={isSyncing}
						isReadOnly={isReadOnlyMentionTab}
						focusedMentionId={focusedMentionId}
						editingMentionId={editingMentionId}
						editingComment={editingComment}
						overrides={mentionOverrides}
						priorityFilter={priorityFilter}
						dueDateFilter={dueDateFilter}
						senderFilter={senderFilter}
						sessionFilter={sessionFilter}
						searchKeyword={searchKeyword}
						senderOptions={senderOptions}
						priorityFilterCounts={priorityFilterCounts}
						dueDateFilterCounts={dueDateFilterCounts}
						senderFilterCounts={senderFilterCounts}
						sessionFilterCounts={sessionFilterCounts}
						onTogglePriorityFilter={handleTogglePriorityFilter}
						onToggleDueDateFilter={handleToggleDueDateFilter}
						onToggleSenderFilter={handleToggleSenderFilter}
						onSelectAllSenders={handleSelectAllSenders}
						onClearSenderFilter={handleClearSenderFilter}
						onToggleSessionFilter={handleToggleSessionFilter}
						onChangeRcFilter={handleChangeRcFilter}
						onChangeSearchKeyword={setSearchKeyword}
						onResetFilters={() => {
							setPriorityFilter([])
							setDueDateFilter([])
							setSenderFilter(null)
							setSessionFilter(DEFAULT_SESSION_FILTER)
							setSearchKeyword("")
						}}
						onFocusMention={setFocusedMentionId}
						onComplete={handleCompleteMentionOnly}
						onCompleteAll={handleCompleteMentionAll}
						onSetTomorrow={handleSetTomorrow}
						onSetDueDate={handleSetDueDate}
						onPriorityUp={handlePriorityUpMention}
						onPriorityDown={handlePriorityDownMention}
						onStartEditComment={handleStartEditComment}
						onSaveComment={handleSaveComment}
						onCancelEditComment={handleCancelEditComment}
						onChangeEditingComment={setEditingComment}
						onOpenSlack={handleOpenSlack}
						slackAppSetting={slackAppSetting}
						onChangeSlackAppEnabled={handleChangeSlackAppEnabled}
						onSyncMentions={handleSyncMentions}
						onCreateMention={handleOpenCreateModal}
						onToggleChildNodesCollapsed={handleToggleChildNodesCollapsed}
						onAddRootNode={handleAddRootNode}
						onHandOverEmbedSession={handleOpenHandOver}
						onUpdateNode={handleUpdateNode}
						onArchiveNode={handleArchiveNode}
						onAddChildNode={handleAddChildNode}
						onNodePriorityUp={handleNodePriorityUp}
						onNodePriorityDown={handleNodePriorityDown}
						onNodeCreateSession={handleNodeCreateSession}
						onNodeCompleteSession={handleNodeCompleteSession}
						onNodePendSession={handleNodePendSession}
						onNodeOpenSlack={handleNodeOpenSlack}
						onEmbeddedSessionOpenSlack={handleEmbeddedSessionOpenSlack}
						onEmbeddedSessionFork={handleEmbeddedSessionFork}
						onNodeCopyBranch={handleNodeCopyBranch}
						onNodeForkSession={handleNodeForkSession}
						onNodeOpenMemo={handleNodeOpenMemo}
						onNodeMove={handleNodeMove}
						onNodeReorder={handleNodeReorder}
						onNodeFocusEscapeUp={handleNodeFocusEscapeUp}
						onNodeFocusEscapeDown={handleNodeFocusEscapeDown}
						nodeTreeFocusRequest={nodeTreeFocusRequest}
						onNodeFocusChange={handleNodeFocusChange}
					/>
					{isCreateModalOpen && (
						<CreateMentionModal
							defaultPriority={createMentionDefaultPriority}
							isCreating={isCreating}
							onClose={handleCloseCreateModal}
							onCreate={handleCreateMention}
						/>
					)}
					{/* 期限設定モーダル */}
					{dueDateModalMentionId && (
						<SetDueDateModal
							onClose={handleCloseDueDateModal}
							onSubmit={(daysFromToday) =>
								handleSubmitDueDate(dueDateModalMentionId, daysFromToday)
							}
						/>
					)}
					{/* Session 作成 / node 新規作成モーダル (shared component) */}
					{nodeSessionModal &&
						(nodeSessionModal.kind === "existingNode" ? (
							<CreateSessionModal
								nodeId={nodeSessionModal.nodeId}
								nodeName={nodeSessionModal.nodeName}
								onClose={() => setNodeSessionModal(null)}
								onSubmit={handleNodeSessionSubmit}
								onAttach={handleNodeAttachSlackThread}
								isLoading={createSessionLoading}
								isAttachLoading={attachLoading}
							/>
						) : (
							<CreateSessionModal
								isCreateNodeMode
								onClose={() => setNodeSessionModal(null)}
								onCreateNodeOnly={handleCreateNodeOnly}
								onCreateNodeWithSession={handleCreateNodeWithSession}
								isLoading={
									createSessionLoading ||
									createRootNodeLoading ||
									createChildNodeLoading
								}
							/>
						))}
					{/* メモ編集モーダル (shared component) */}
					{nodeMemoModal && (
						<MemoEditModal
							nodeId={nodeMemoModal.id}
							nodeName={nodeMemoModal.name}
							currentMemo={nodeMemoModal.session?.memo ?? null}
							onClose={() => setNodeMemoModal(null)}
							onSubmit={handleNodeMemoSubmit}
							isLoading={updateMemoLoading}
						/>
					)}
					{/* embed 引き継ぎモーダル (shared component) */}
					{handOverMentionId && (
						<HandOverEmbedSessionModal
							onClose={() => setHandOverMentionId(null)}
							onSubmit={handleSubmitHandOver}
							isLoading={handOverLoading}
						/>
					)}
				</div>
			)}
		</section>
	)
}

export default TaigaMentionsSection
